import { useState } from 'react';
import { People } from '@material-ui/icons';

import { makeStyles, createStyles, Theme } from 'core/atoms/styles';

import { Box } from 'core/cells/box';
import { Grid } from 'core/cells/grid';
import { LinearProgress } from 'core/cells/linear-progress';

import { useTranslations } from 'core/dna/translations';

import { Button } from 'templates/mdp/components/custom-buttons';
import { GridContainer, GridItem } from 'templates/mdp/components/grid';
import {
  Card,
  CardBody,
  CardHeader,
  CardIcon,
} from 'templates/mdp/components/card';

import { WeekCalendar } from 'core/organisms/week-calendar';
import { RouteLeavingGuard } from 'modules/planner/organisms/route-leaving-guard';
import {
  useDayResourceSelector,
  DayResourceSelector,
} from 'modules/planner/organisms/day-resource-selector';
import {
  TimeSlotDurationSelector,
  Duration,
} from 'modules/planner/organisms/time-slot-duration-selector';

import { useClassesAvailability } from './use-classes-availability';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(2),
    },
    resourcesRow: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: theme.spacing(2),
    },
    runButton: {
      marginRight: theme.spacing(0.5),
    },
    clearButton: {
      marginLeft: theme.spacing(0.5),
    },
    h400: {
      // height: 400,
    },
    breakEvent: {
      background: `
        repeating-linear-gradient(
          45deg,
          ${theme.palette.secondary.main},
          ${theme.palette.secondary.main} 10px,
          ${theme.palette.secondary.dark} 10px,
          ${theme.palette.secondary.dark} 20px
        )
      `,
      borderColor: theme.palette.secondary.dark,
      color: theme.palette.secondary.contrastText,
      '& .fc-content .fc-delete': {
        display: 'none',
      },
      '&:hover': {
        '& .fc-content .fc-delete': {
          display: 'block',
          position: 'absolute',
          top: 0,
          right: 0,
        },
      },
    },
  }),
);

export const ClassesAvailability = () => {
  const classes = useStyles();
  const { texts } = useTranslations();

  const {
    startTime,
    endTime,
    resources,
    bgEvents,
    events,
    add,
    edit,
    save,
    saving,
    dirty,
    loading,
    error,
    eventContent,
  } = useClassesAvailability();

  const {
    dayResourceSelectorValue,
    handleViewChange,
  } = useDayResourceSelector();

  const [timeSlotDurationValue, setTimeSlotDurationValue] = useState(
    Duration.M05,
  );

  if (loading) return <p>Loading Classes...</p>;
  if (error) return <p>Classes Error!</p>;

  return (
    <GridContainer>
      <GridItem xs={12}>
        <Card>
          <CardHeader color="primary" icon>
            <CardIcon color="primary">
              <People />
            </CardIcon>
          </CardHeader>
          <CardBody>
            <RouteLeavingGuard
              when={dirty}
              shouldBlockNavigation={() => true}
            />
            <Grid container spacing={2}>
              <Grid item xs={12} className={classes.resourcesRow}>
                <DayResourceSelector
                  onChange={handleViewChange}
                  resourceName={texts.planner.common.viewClasses}
                  value={dayResourceSelectorValue}
                />
                <Box ml={1}>
                  <TimeSlotDurationSelector
                    value={timeSlotDurationValue}
                    onChange={setTimeSlotDurationValue}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} className={classes.h400}>
                <WeekCalendar
                  type="resource-week"
                  slotLabelInterval="00:05"
                  slotDuration={timeSlotDurationValue}
                  snapDuration="00:05"
                  slotMinTime={startTime}
                  slotMaxTime={endTime}
                  resources={resources}
                  datesAboveResources={dayResourceSelectorValue === 'days'}
                  bgEvents={bgEvents}
                  events={events}
                  eventTitle={texts.planner.common.breaks.many}
                  eventClass={classes.breakEvent}
                  onAdd={add}
                  onEdit={edit}
                  contentHeight="auto"
                  eventContent={eventContent}
                />
              </Grid>
              <Grid item xs={12}>
                {saving && <LinearProgress />}
                <Box centeredContent>
                  <Button
                    color="secondary"
                    className={classes.runButton}
                    onClick={save}
                    disabled={!dirty}
                  >
                    {texts.planner.common.save}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
};
